import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ErrorMessage from 'components/ErrorMessage';

const TemplateFormModal = ({ setShowModal, onSave, initialData }) => {
  const { t } = useTranslation();
  const isEditing = !!initialData;
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      role: initialData?.role || '',
      duration: initialData?.duration || 30,
      skills: initialData?.skills?.join(', ') || '',
      difficulty: initialData?.difficulty || 'medium',
      instructions: initialData?.instructions || '',
      totalQuestions: initialData?.totalQuestions || 6,
    },
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    await onSave({
      role: data.role,
      duration: Number(data.duration),
      skills: data.skills
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
      difficulty: data.difficulty,
      instructions: data.instructions,
      totalQuestions: Number(data.totalQuestions),
    });
    setIsSubmitting(false);
  };

  return (
    <>
      <div className="justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none">
        <div className="relative w-auto my-6 mx-auto max-w-3xl">
          <form className="sm:py-4 sm:px-2 py-8 px-7" onSubmit={handleSubmit(onSubmit)}>
            <div className="border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none min-w-[600px]">
              <div className="flex items-center justify-between p-5 border-b border-solid border-slate-200 rounded-t">
                <h3 className="text-xl font-semibold">
                  {isEditing ? 'Edit Interview Template' : 'New Interview Template'}
                </h3>
                <button
                  type="button"
                  className="ml-auto flex items-center justify-center text-black border-2 rounded-full h-8 w-8 float-right text-3xl leading-none font-extralight outline-none focus:outline-none"
                  onClick={() => setShowModal(false)}
                >
                  <span className="text-[#B8BBBF] text-4xl" title="Close">×</span>
                </button>
              </div>

              <div className="relative p-6 flex-auto">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="w-full">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Role<span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. Backend Engineer"
                      className="block w-full p-3 text-sm text-gray-900 border-2 rounded-lg border-[#DFDFDF] focus:outline-none focus:ring-0"
                      {...register('role', { required: 'Role is required.' })}
                    />
                    {errors?.role && <ErrorMessage message={errors.role.message} />}
                  </div>

                  <div className="w-full">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Difficulty<span className="text-red-500">*</span>
                    </label>
                    <select
                      className="block w-full p-3 text-sm text-gray-900 border-2 rounded-lg border-[#DFDFDF] focus:outline-none focus:ring-0"
                      {...register('difficulty', { required: true })}
                    >
                      <option  key="easy" value="easy">Easy</option>
                      <option  key="medium" value="medium">Medium</option>
                      <option  key="hard" value="hard">Hard</option>
                    </select>
                  </div>

                  <div className="w-full sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Skills (comma-separated)<span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. Node.js, MongoDB, System Design"
                      className="block w-full p-3 text-sm text-gray-900 border-2 rounded-lg border-[#DFDFDF] focus:outline-none focus:ring-0"
                      {...register('skills', { required: 'At least one skill is required.' })}
                    />
                    {errors?.skills && <ErrorMessage message={errors.skills.message} />}
                  </div>

                  <div className="w-full">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Duration (minutes)<span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      min={5}
                      className="block w-full p-3 text-sm text-gray-900 border-2 rounded-lg border-[#DFDFDF] focus:outline-none focus:ring-0"
                      {...register('duration', { required: true, min: 5 })}
                    />
                  </div>

                  <div className="w-full">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Total Questions<span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      min={1}
                      max={20}
                      className="block w-full p-3 text-sm text-gray-900 border-2 rounded-lg border-[#DFDFDF] focus:outline-none focus:ring-0"
                      {...register('totalQuestions', { required: true, min: 1, max: 20 })}
                    />
                  </div>

                  <div className="w-full sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Additional Instructions (optional)
                    </label>
                    <textarea
                      rows={3}
                      placeholder="e.g. Focus more on system design than syntax trivia."
                      className="block w-full p-3 text-sm text-gray-900 border-2 rounded-lg border-[#DFDFDF] focus:outline-none focus:ring-0"
                      {...register('instructions')}
                    />
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-center p-6 border-t border-solid border-slate-200 rounded-b">
                <button
                  type="button"
                  className="text-black bg-[#E1E1E1] font-normal px-12 py-2.5 text-sm outline-none focus:outline-none rounded mr-6 ease-linear transition-all duration-150"
                  onClick={() => setShowModal(false)}
                >
                  {t('O_BACK') || 'Back'}
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-LightBlue text-white active:bg-emerald-600 font-normal text-sm px-8 py-2.5 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 ease-linear transition-all duration-150 disabled:opacity-50"
                >
                  {isSubmitting ? 'Saving…' : isEditing ? 'Save Changes' : 'Create Template'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
      <div className="opacity-25 fixed inset-0 z-40 bg-black" />
    </>
  );
};

export default TemplateFormModal;